'use client'

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Zap } from 'lucide-react'
import axios from 'axios'
import { useBike } from '../context/BikeContext'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

export const BikeSelectionModal = ({
  isOpen,
  onClose,
  onBikeSelect,
}) => {
  const { selectedBike } = useBike()

  const [brands, setBrands] = useState([])
  const [models, setModels] = useState([])
  const [variants, setVariants] = useState([])

  const [selectedBrand, setSelectedBrand] = useState('')
  const [selectedModel, setSelectedModel] = useState('')
  const [selectedVariant, setSelectedVariant] = useState('')

  const [loading, setLoading] = useState(false)

  /* --------------------------------------------------- */
  /* 🔥 Sync modal state when opened */
  /* --------------------------------------------------- */
  useEffect(() => {
    if (!isOpen) return

    if (selectedBike) {
      setSelectedBrand(selectedBike.brand || '')
      setSelectedModel(selectedBike.model || '')
      setSelectedVariant(selectedBike.variant || '')
    } else {
      // ✅ Reset completely if bike removed
      setSelectedBrand('')
      setSelectedModel('')
      setSelectedVariant('')
      setModels([])
      setVariants([])
    }
  }, [isOpen, selectedBike])

  /* --------------------------------------------------- */
  /* Fetch brands when modal opens */
  /* --------------------------------------------------- */
  useEffect(() => {
    if (isOpen) fetchBrands()
  }, [isOpen])

  /* --------------------------------------------------- */
  /* Fetch models when brand changes */
  /* --------------------------------------------------- */
  useEffect(() => {
    if (!selectedBrand) {
      setModels([])
      setSelectedModel('')
      setVariants([])
      setSelectedVariant('')
      return
    }

    fetchModels(selectedBrand)
  }, [selectedBrand])

  /* --------------------------------------------------- */
  /* Fetch variants when model changes */
  /* --------------------------------------------------- */
  useEffect(() => {
    if (!selectedModel) {
      setVariants([])
      setSelectedVariant('')
      return
    }

    fetchVariants(selectedBrand, selectedModel)
  }, [selectedModel])

  /* --------------------------------------------------- */
  /* API Calls */
  /* --------------------------------------------------- */
  const fetchBrands = async () => {
    try {
      setLoading(true)
      const res = await axios.get(`${API}/bikes/brands`)
      setBrands(res.data || [])
    } catch (e) {
      console.error('Error fetching brands:', e)
    } finally {
      setLoading(false)
    }
  }

  const fetchModels = async (brand) => {
    try {
      const res = await axios.get(
        `${API}/bikes/models/${encodeURIComponent(brand)}`
      )
      setModels(res.data || [])
    } catch (e) {
      console.error('Error fetching models:', e)
      setModels([])
    }
  }

  const fetchVariants = async (brand, model) => {
    try {
      const res = await axios.get(
        `${API}/bikes/variants/${encodeURIComponent(
          brand
        )}/${encodeURIComponent(model)}`
      )
      setVariants(res.data || [])
    } catch (e) {
      console.error('Error fetching variants:', e)
      setVariants([])
    }
  }

  /* --------------------------------------------------- */
  /* Select Bike */
  /* --------------------------------------------------- */
  const handleSelectBike = () => {
    if (!selectedBrand || !selectedModel || !selectedVariant) return

    onBikeSelect(selectedBrand, selectedModel, selectedVariant)
    onClose()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
          >
            <div className="w-full max-w-lg bg-black border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden">

              {/* Header */}
              <div className="px-6 py-6 border-b border-zinc-800 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
                    <Zap size={22} className="text-black" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      {selectedBike ? 'Change Your Bike' : 'Add Your Bike'}
                    </h2>
                    <p className="text-zinc-400 text-xs">
                      Select your bike to find compatible parts
                    </p>
                  </div>
                </div>

                <button
                  onClick={onClose}
                  className="text-zinc-400 hover:text-white transition"
                >
                  <X size={22} />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-5">

                {/* Brand */}
                <div>
                  <label className="block text-sm text-white mb-2">
                    Select Make / Brand
                  </label>
                  <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                    <SelectTrigger className="bg-zinc-900 border border-zinc-700 text-white">
                      <SelectValue placeholder="Choose your bike brand..." />
                    </SelectTrigger>
                    <SelectContent className="bg-black border border-zinc-700">
                      {brands.map((brand) => (
                        <SelectItem
                          key={brand.id || brand}
                          value={brand.name || brand}
                          className="text-white"
                        >
                          {brand.name || brand}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Model */}
                <div>
                  <label className="block text-sm text-white mb-2">
                    Select Model
                  </label>
                  <Select
                    value={selectedModel}
                    onValueChange={setSelectedModel}
                    disabled={!selectedBrand}
                  >
                    <SelectTrigger className="bg-zinc-900 border border-zinc-700 text-white">
                      <SelectValue placeholder="Choose your model..." />
                    </SelectTrigger>
                    <SelectContent className="bg-black border border-zinc-700">
                      {models.map((model) => (
                        <SelectItem
                          key={model.id || model}
                          value={model.name || model}
                          className="text-white"
                        >
                          {model.name || model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Variant */}
                <div>
                  <label className="block text-sm text-white mb-2">
                    Select Variant
                  </label>
                  <Select
                    value={selectedVariant}
                    onValueChange={setSelectedVariant}
                    disabled={!selectedModel}
                  >
                    <SelectTrigger className="bg-zinc-900 border border-zinc-700 text-white">
                      <SelectValue placeholder="Choose your variant..." />
                    </SelectTrigger>
                    <SelectContent className="bg-black border border-zinc-700">
                      {variants.map((variant) => (
                        <SelectItem
                          key={variant.id || variant}
                          value={variant.name || variant}
                          className="text-white"
                        >
                          {variant.name || variant}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Preview */}
                {selectedBrand && selectedModel && selectedVariant && (
                  <div className="p-4 bg-zinc-900 border border-orange-500/40 rounded-lg">
                    <p className="text-xs text-zinc-400 mb-1">
                      Selected Bike:
                    </p>
                    <p className="text-orange-400 font-bold">
                      {selectedBrand} {selectedModel} - {selectedVariant}
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-zinc-800 flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition"
                >
                  Cancel
                </button>

                <button
                  onClick={handleSelectBike}
                  disabled={!selectedBrand || !selectedModel || !selectedVariant}
                  className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-400 text-black font-bold rounded-lg transition disabled:opacity-50"
                >
                  {selectedBike ? 'Update Bike' : 'Set Bike'}
                </button>
              </div>

            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
